import { AuthContext, toCamel } from '@repo-level/core-front';
import type { CMSImageBase } from 'api/cms/base';
import { CMSGeneralButtonType } from 'api/cms/base';
import type { BonusGroupItem, BonusItemGeneral } from 'api/cms/bonuses';
import { Button } from 'components/buttons';

import { ImageComponent } from 'components/Image';
import { Slider } from 'components/Slider';
import { TimerLabel } from 'components/Timer';
import { AnimatePresence, motion } from 'framer-motion';
import { getTimeLeftFromScheduler } from 'helpers/timer';
import { useHandleBonus } from 'hooks/bonuses';
import { useFranceLocale } from 'hooks/franceLocale';
import { useTheme } from 'hooks/theme';
import {
  BONUS_REF_PARAM,
  checkHasTermsAndConditions,
} from 'modules/bonuses/helpers';
import { useMedia } from 'modules/common/hooks/useMedia';
import { findIndex } from 'ramda';
import type { FC } from 'react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentRoute } from 'react-navi';
import type { ScShowMoreBtnProps } from './styled';
import {
  ScActiveBonusDetails,
  ScBonus,
  ScBonusCard,
  ScBonusesGroup,
  ScMoreBtn,
  ScTermsAndConditions,
} from './styled';

export interface IBonusCardProps {
  title: string;
  offer: string;

  background: CMSImageBase | null;
  character: CMSImageBase | null;

  activeBonus?: BonusItemGeneral;
  bonuses?: BonusGroupItem[];
}

export const BonusCard: FC<IBonusCardProps> = ({
  title,
  offer,
  background,
  character,
  bonuses,
  activeBonus,
}) => {
  const { t } = useTranslation('glossary');

  const {
    url: { query },
  } = useCurrentRoute();

  const {
    minDepIcon: MinDepIcon,
    wagerIcon: WagerIcon,
    frequencyIcon: FrequencyIcon,
    maxBonusAmountIcon: MaxBonusAmountIcon,
    additionalInfoIcon: AdditionalInfoIcon,
    depNumberIcon: DepNumberIcon,
    fsAmountIcon: FsAmountIcon,
    gameIcon: GameIcon,
    maxWinFsIcon: MaxWinFsIcon,
    wagerTimeIcon: WagerTimeIcon,
  } = useTheme();

  const { isMobileS } = useMedia();
  const { isAuthenticated } = useContext(AuthContext);

  const { isFRWithoutStag } = useFranceLocale();

  const [isTermsAndConditionsOpen, toggleTermsAndConditionsOpen] =
    useState(false);

  const ref = useRef<HTMLDivElement>(null);

  const [isBonusSelected, setIsBonusSelected] = useState<boolean>(false);

  const {
    description,
    frequency,
    wager,
    minDep,
    maxBonusAmount,
    additionalInfo,
    depNumber,
    fsAmount,
    game,
    maxWinFs,
    wagerTime,
    termsAndConditions,
    code,
    id: activeBonusId,
    buttonName,
    buttonLink,
    buttonType,
    scheduler,
    countdownToggle,
    identifier,
  } = activeBonus || {};

  const { handleBonus, pendingBonus } = useHandleBonus(code);

  const timer = useMemo(
    () => (countdownToggle ? getTimeLeftFromScheduler(scheduler) : null),
    [scheduler, countdownToggle]
  );

  const hasTermsAndConditions = useMemo(
    () => checkHasTermsAndConditions(termsAndConditions),
    [termsAndConditions]
  );

  const bonusButtonParams = useMemo(() => {
    const getText = (trans: string) => {
      if (buttonName == null || buttonName === '')
        return t(`${toCamel(trans)}`);

      return buttonName;
    };

    const getLink = (() => {
      if (buttonLink == null || buttonLink === '') return;
      return buttonLink;
    })();

    switch (buttonType) {
      case CMSGeneralButtonType.DEPOSIT:
        return {
          text: `${
            isAuthenticated
              ? getText('useBonus')
              : isFRWithoutStag
                ? t('login')
                : t('register')
          }`,
          click: handleBonus,
        };
      case CMSGeneralButtonType.SHOW_MORE:
        return {
          text: getText('details'),
          link: getLink,
        };
      case CMSGeneralButtonType.EXPLORE:
        return {
          text: getText(CMSGeneralButtonType.EXPLORE),
          link: getLink,
        };
      case CMSGeneralButtonType.READ_MORE:
        return {
          text: getText(CMSGeneralButtonType.READ_MORE),
          link: getLink,
        };
      case CMSGeneralButtonType.JOIN_NOW:
        return {
          text: getText(CMSGeneralButtonType.JOIN_NOW),
          link: getLink,
        };
      case CMSGeneralButtonType.PLAY_NOW:
        return {
          text: getText(CMSGeneralButtonType.PLAY_NOW),
          link: getLink,
        };
      default:
        return {
          text: getText('useBonus'),
          click: handleBonus,
        };
    }
  }, [
    buttonType,
    buttonName,
    t,
    buttonLink,
    isAuthenticated,
    isFRWithoutStag,
    handleBonus,
  ]);

  useEffect(() => {
    if (query[BONUS_REF_PARAM] && query[BONUS_REF_PARAM] === identifier) {
      setIsBonusSelected(true);
    } else {
      setIsBonusSelected(false);
    }
  }, [query, identifier, isAuthenticated]);

  useEffect(() => {
    if (isBonusSelected) {
      window.scrollTo({
        behavior: 'smooth',
        top: (ref?.current?.getBoundingClientRect().top || 0) - 64,
      });
    }
  }, [isBonusSelected, isAuthenticated]);

  return (
    <ScBonusCard
      banner={background?.url}
      isGroup={!!bonuses}
      offerLength={offer.length}
      ref={ref}
      titleLength={title.length}
    >
      <a href={bonusButtonParams.link} onClick={bonusButtonParams.click}>
        <div className="banner">
          <div className="content">
            <header>
              <div className="title">{title}</div>
              <div className="offer">{offer}</div>
            </header>

            {bonuses && (
              <BonusesGroup activeBonusId={activeBonusId} bonuses={bonuses} />
            )}

            <Button blue pending={pendingBonus} wide={!isMobileS} xLarge>
              {bonusButtonParams.text}
            </Button>
          </div>
          <div className="image-container">
            <ImageComponent
              alt="character"
              className="image"
              height={240}
              src={character?.url || ''}
              width={288}
            />
          </div>
        </div>
      </a>

      <ScActiveBonusDetails withPaddingBottom={!hasTermsAndConditions}>
        <div className="inner-container">
          {description && (
            <div
              className="description"
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          )}
          <div className="details">
            {!!timer && (
              <div className="details__item timer">
                <FrequencyIcon />
                <div className="details__item__value">
                  {t('expiringIn')} <TimerLabel timeLeft={timer} />
                </div>
              </div>
            )}
            {frequency && (
              <div className="details__item">
                <FrequencyIcon />
                <span className="details__item__value">{frequency}</span>
              </div>
            )}
            {wager && (
              <div className="details__item">
                <WagerIcon />
                <span className="details__item__value">{wager}</span>
              </div>
            )}
            {minDep && (
              <div className="details__item">
                <MinDepIcon />
                <span className="details__item__value">{minDep}</span>
              </div>
            )}
            {maxBonusAmount && (
              <div className="details__item">
                <MaxBonusAmountIcon />
                <span className="details__item__value">{maxBonusAmount}</span>
              </div>
            )}
            {additionalInfo && (
              <div className="details__item">
                <AdditionalInfoIcon />
                <span className="details__item__value">{additionalInfo}</span>
              </div>
            )}
            {depNumber && (
              <div className="details__item">
                <DepNumberIcon />
                <span className="details__item__value">{depNumber}</span>
              </div>
            )}
            {fsAmount && (
              <div className="details__item">
                <FsAmountIcon />
                <span className="details__item__value">{fsAmount}</span>
              </div>
            )}
            {game && (
              <div className="details__item">
                <GameIcon />
                <span className="details__item__value">{game}</span>
              </div>
            )}
            {maxWinFs && (
              <div className="details__item">
                <MaxWinFsIcon />
                <span className="details__item__value">{maxWinFs}</span>
              </div>
            )}
            {wagerTime && (
              <div className="details__item">
                <WagerTimeIcon />
                <span className="details__item__value">{wagerTime}</span>
              </div>
            )}

            {hasTermsAndConditions && (
              <ShowMoreBtn
                isListItem
                isOpen={isTermsAndConditionsOpen}
                onClick={() => toggleTermsAndConditionsOpen((s) => !s)}
              />
            )}
          </div>

          {termsAndConditions && hasTermsAndConditions && (
            <AnimatePresence>
              <ScTermsAndConditions
                animate={isTermsAndConditionsOpen ? 'open' : 'closed'}
                as={motion.div}
                initial={{ height: isMobileS ? '72px' : '48px' }}
                isOpen={isTermsAndConditionsOpen}
                transition={{ duration: 0.3 }}
                variants={{
                  closed: {
                    height: isMobileS ? '72px' : '48px',
                  },
                  open: { height: 'auto' },
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: termsAndConditions,
                  }}
                />
              </ScTermsAndConditions>
            </AnimatePresence>
          )}
        </div>
      </ScActiveBonusDetails>
    </ScBonusCard>
  );
};

interface ShowMoreBtnProps extends ScShowMoreBtnProps {
  onClick: (args: any) => void;
}

export const ShowMoreBtn: FC<ShowMoreBtnProps> = ({
  isOpen,
  onClick,
  isListItem,
}) => {
  const { t } = useTranslation('glossary');

  const { images } = useTheme();

  return (
    <ScMoreBtn isListItem={isListItem} isOpen={isOpen} onClick={onClick}>
      <span>
        {isOpen ? t('less') : t('more')}
        <images.arrowRightSmallIcon.component />
      </span>
    </ScMoreBtn>
  );
};

interface BonusesGroupProps {
  bonuses: BonusGroupItem[];
  activeBonusId?: number;
}

const BonusesGroup: FC<BonusesGroupProps> = ({ bonuses, activeBonusId }) => {
  const { numbers } = useTheme();

  const { isMobileS } = useMedia();

  const bonusesData = useMemo(
    () =>
      bonuses.map((bonus, index) => {
        const NumberIcon =
          numbers[
            `number-${activeBonusId === bonus.id ? 'shine-' : ''}${index + 1}`
          ].component;

        return {
          ...bonus,
          icon: <NumberIcon />,
        };
      }),
    [bonuses, activeBonusId, numbers]
  );

  const activeBonusIndex = useMemo(
    () => findIndex(({ id }) => id === activeBonusId, bonusesData),
    [bonusesData, activeBonusId]
  );

  return (
    <ScBonusesGroup>
      {isMobileS ? (
        <Slider
          initialSlide={activeBonusIndex}
          slide={({ id, offer, icon, isActive }, idx) => (
            <ScBonus
              isActive={activeBonusId === id}
              isUsed={!!isActive && idx < activeBonusIndex}
              key={id}
            >
              {icon}
              <span>{offer}</span>
            </ScBonus>
          )}
          slides={bonusesData}
          slidesPerView={1.8}
          spaceBetween={8}
          speed={800}
        />
      ) : (
        bonusesData.map(({ id, offer, icon, isActive }, idx) => (
          <ScBonus
            isActive={activeBonusId === id}
            isUsed={!!isActive && idx < activeBonusIndex}
            key={id}
          >
            {icon}
            {offer}
          </ScBonus>
        ))
      )}
    </ScBonusesGroup>
  );
};
